import CommentIcon from '@mui/icons-material/Comment';
import {
  Badge,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AsyncContent } from '~/components/AsyncContent';
import { SURVEY_STATUS } from '~/data/surveys';

import { AnswerSurveyFormDialog } from './AnswerSurveyFormDialog';
import { SurveyResultsDialog } from './SurveyResults';
import { useSxStyles } from './useStyles';
import { useTopbarSurvey } from './useTopbarSurvey';
type Props = {
  style: Object;
};

export const TopbarSurvey: React.FC<Props> = ({ style }) => {
  const {
    notificationsLoading,
    failedToLoadNotifications,
    notifications,
    answerFeelingSurvey,
    pulseAnswered,
    anchorEl,
    open,
    getBadgeCount,
    handleSurveyOpen,
    handleSurveyClose,
    handleAnswer,
    handleResults,
    handleAnswerClose,
    resultId,
    answerId,
    handleResultsClose,
  } = useTopbarSurvey();

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const renderNotifications = () => {
    if (!notifications || notifications.length === 0) {
      return (
        <Typography variant="body2">
          {t('text_no_available_surveys')}
        </Typography>
      );
    }
    return (
      <AsyncContent
        isLoading={notificationsLoading}
        failedToLoad={failedToLoadNotifications}
        noData={!notifications || notifications.length === 0}
        noDataMessage={t('text_no_available_surveys')}
      >
        {(notifications || []).map((notif) =>
          notif.status === SURVEY_STATUS.PUBLISHED ||
          notif.status === SURVEY_STATUS.ACTIVE ? (
            <Grid
              container
              item
              xs={12}
              alignItems="center"
              justifyContent="space-between"
              key={notif.id}
              sx={sxStyles.listItem}
            >
              <Typography variant="body2" sx={sxStyles.listItemTitle}>
                {notif.name}
                {notif.status === SURVEY_STATUS.PUBLISHED
                  ? `(${t('label_PUBLISHED')})`
                  : ''}
              </Typography>
              {notif.status === SURVEY_STATUS.PUBLISHED ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleResults(notif.id)}
                >
                  {t('label_results')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleAnswer(notif.id)}
                >
                  {t('label_answer')}
                </Button>
              )}
            </Grid>
          ) : null,
        )}
      </AsyncContent>
    );
  };

  return (
    <>
      <Tooltip title={`${t('label_available_surveys')}`}>
        <Box
          onClick={handleSurveyOpen}
          aria-label={t('label_available_surveys')}
          sx={{ ...style, ...sxStyles.iconButton }}
        >
          <Badge badgeContent={getBadgeCount()} color="error">
            <CommentIcon />
          </Badge>
        </Box>
      </Tooltip>
      <Popover
        id={open ? 'simple-popover' : undefined}
        open={open}
        anchorEl={anchorEl}
        onClose={handleSurveyClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <>
          <Typography variant="h6" sx={sxStyles.contentTitle}>
            {t('laCompFeelSurveyTitle')}
          </Typography>
          <Paper sx={sxStyles.pulsePaper}>
            {pulseAnswered ? (
              <Typography sx={sxStyles.pulseAnswered} variant="body1">
                {t('laThanksForAnsweringPulse')}
              </Typography>
            ) : (
              <>
                <IconButton
                  sx={sxStyles.pulseButton}
                  onClick={() => answerFeelingSurvey(1)}
                  size="large"
                >
                  <i
                    style={{ color: '#FF3300' }}
                    className="fa fa-2x fa-frown-o"
                  />
                </IconButton>
                <IconButton
                  sx={sxStyles.pulseButton}
                  onClick={() => answerFeelingSurvey(2)}
                  size="large"
                >
                  <i
                    style={{ color: '#FFD200' }}
                    className="fa fa-2x fa-meh-o"
                  />
                </IconButton>
                <IconButton
                  sx={sxStyles.pulseButton}
                  onClick={() => answerFeelingSurvey(3)}
                  size="large"
                >
                  <i
                    style={{ color: '#92D050' }}
                    className="fa fa-2x fa-smile-o"
                  />
                </IconButton>
              </>
            )}
          </Paper>
          <Typography variant="h6" sx={sxStyles.contentTitle}>
            {t('title_surveys')}
          </Typography>
          <Grid container direction="row" sx={sxStyles.content}>
            {renderNotifications()}
          </Grid>
        </>
      </Popover>
      {answerId && (
        <AnswerSurveyFormDialog
          handleAnswerClose={handleAnswerClose}
          surveyId={answerId}
        />
      )}
      {resultId && (
        <SurveyResultsDialog
          handleResultsClose={handleResultsClose}
          resultId={resultId}
        />
      )}
    </>
  );
};
